import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ScoreTimeSeriesService } from 'src/app/shared/services/data-api/score-time-series.service';
import { RawScorePoint } from './chart-data.models';
import {
  ScoreType,
  ScoreRequestTypes,
  ScoreTimeSeriesEntityInfo,
} from 'src/app/shared/models/score-time-series.interface';
import { ScoreUtilsService } from 'src/app/shared/utils/score-utils.service';
import { DateUtils } from 'src/app/shared/utils/date.utils';
import getScoreTypeDetails from 'src/app/shared/utils/score-type-details';

export interface RankingScoreResult {
  entity_info: ScoreTimeSeriesEntityInfo;
  lastScore: number | null;
}

@Injectable({
  providedIn: 'root',
})
export class ChatChartsApiService {
  constructor(private scoreTimeSeriesService: ScoreTimeSeriesService) {}

  fetchScoreTimeSeries(
    scoreType: ScoreType,
    entity: string,
    time: string,
  ): Observable<{ score_ts: RawScorePoint[] }> {
    return this.scoreTimeSeriesService
      .getScoreTimeSeries({
        customParams: { collapse_on: ['participant'] },
        body: {
          score_type: scoreType,
          entity: [entity],
          entityType: 'company',
          time: time,
        },
      })
      .pipe(
        map((response) => ({
          score_ts: this.extractScoreTimeSeries(response),
        })),
        catchError(() => of({ score_ts: [] })),
      );
  }

  fetchSentimentTimeSeries(
    entity: string,
    time: string,
    period: number,
  ): Observable<{ score_ts: RawScorePoint[] }> {
    return this.scoreTimeSeriesService
      .getScoreTimeSeries({
        customParams: { collapse_on: ['participant'] },
        body: {
          score_type: { period },
          entity: [entity],
          entityType: 'company',
          time: time,
        },
      })
      .pipe(
        map((response) => ({
          score_ts: this.extractScoreTimeSeries(response),
        })),
        catchError(() => of({ score_ts: [] })),
      );
  }

  fetchRankingScores(
    entities: string[],
    scoreTypeId: string,
    time: string,
  ): Observable<RankingScoreResult[]> {
    const resolvedScoreType = this.resolveRankingScoreType(scoreTypeId, time);

    return this.scoreTimeSeriesService
      .getScoreTimeSeries({
        customParams: {},
        body: {
          score_type: resolvedScoreType,
          entity: entities,
          time: time,
        },
      })
      .pipe(
        map((response) => {
          return (response.score_ts || []).map((entry) => ({
            entity_info: entry.entity_info,
            lastScore: ScoreUtilsService.getLastScore(entry.score_ts),
          }));
        }),
        catchError(() => of([])),
      );
  }

  private resolveRankingScoreType(
    scoreTypeId: string,
    time: string,
  ): ScoreRequestTypes {
    if (scoreTypeId === 'sentiment') {
      const timeframeLength = DateUtils.chartFromTo(time).period;
      const period = timeframeLength <= 7 ? 1 : timeframeLength <= 30 ? 7 : 30;
      return { period };
    }
    const scoreKey = getScoreTypeDetails(scoreTypeId).scoreKey;
    return scoreKey as ScoreRequestTypes;
  }

  private extractScoreTimeSeries(response: unknown): RawScorePoint[] {
    const results = Array.isArray(response)
      ? response
      : (response as { score_ts?: unknown[] })?.score_ts;
    const raw =
      (results?.[0] as { score_ts?: RawScorePoint[] })?.score_ts || [];

    // Deduplicate by date — the API may return duplicate entries
    const seen = new Set<string>();
    return raw.filter((item) => {
      if (seen.has(item.date)) return false;
      seen.add(item.date);
      return true;
    });
  }
}
